// @ai-generated 2026-10-08T09:40:29+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { useIsPortalSession } from '@Contexts/session';
import { usePortalContainerTypeLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { emptyPage } from '@Hooks/useListApi';
import { useAuftragRepository } from '@Hooks/useRepository';
import { type IHasTableModes } from '@Hooks/useTableModes';
import {
    type ApiPlannedContainerActionGroup,
    type EnumContainerActionRequest,
    EnumFrontendLayoutSetting,
} from '@Model';
import { PlannedContainerActionRepository } from '@Repositories/Auftragsbearbeitung';
import {
    Badge,
    type ExpandedState,
    FloatingToolbar,
    type ListInfo,
    type ListRequest,
    unwrapOr,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import {
    ContainerStockLines,
    FollowUpButtons,
    hasNoLinesToExpand,
    plannedContainerActionColumns,
    plannedContainerActionLineColumns,
    toggledExpansion,
    useContainerSelection,
} from '../Editing';
import { type ContainerStockTableModes } from './containerStockTableModes';
import { useContainerPlanningOnNewDraft } from './useContainerPlanningOnNewDraft';
import { useContainerStockTableModeToolbar } from './useContainerStockTableModeToolbar';

/**
 * The container actions that are planned and not booked yet, grouped by customer, site and container type.
 * Selecting some of one customer at one site offers the follow-ups that can be planned after all of them. The ERP reads the actions of its orders, the portal those of its requisitions.
 */
export function FrameListPlannedContainerActions(tableModeProps: IHasTableModes<ContainerStockTableModes>) {
    const isPortalSession = useIsPortalSession();
    const plannedActions = useAuftragRepository(PlannedContainerActionRepository);
    const { ensure: ensureTypeNames, labelOf: containerTypeOf } = usePortalContainerTypeLabels();
    const navigate = useClientNavigate();
    const openRequisition = useCallback((id: string) => navigate(`/requisitions/${id}`), [navigate]);

    const refetchRef = useRef<() => void>(() => undefined);
    const reload = useCallback(() => refetchRef.current(), []);

    const { canPlan: canFollowUp, isPlanning: isBusy, plan } = useContainerPlanningOnNewDraft({ onSaved: reload });
    const { selectedRows, allowedFollowUps, groupSelector, lineSelectorOf, clear, track } =
        useContainerSelection<ApiPlannedContainerActionGroup>(isBusy);
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const loadPage = useCallback(
        async (request: ListRequest): Promise<ListInfo<ApiPlannedContainerActionGroup>> => {
            const loaded = unwrapOr(
                await plannedActions.getPlannedActionGroups(request),
                emptyPage<ApiPlannedContainerActionGroup>(),
            );
            ensureTypeNames(loaded.items.map((group) => group.container_type));
            track(loaded.items, request.page_no);

            return loaded;
        },
        [plannedActions, ensureTypeNames, track],
    );

    const startFollowUp = useCallback(
        async (kind: EnumContainerActionRequest) => {
            const opened = await plan(kind, { plannedActions: [...selectedRows] });
            if (opened) {
                clear();
            }
        },
        [selectedRows, plan, clear],
    );

    const columns = useMemo(
        () => plannedContainerActionColumns(isPortalSession ? containerTypeOf : undefined),
        [isPortalSession, containerTypeOf],
    );
    const lineColumns = useMemo(
        () => plannedContainerActionLineColumns(isPortalSession ? openRequisition : undefined),
        [isPortalSession, openRequisition],
    );

    const toolbarElements = useContainerStockTableModeToolbar(tableModeProps);

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings<ApiPlannedContainerActionGroup>
                layoutSettingsContext={EnumFrontendLayoutSetting.PlannedContainerActionsTableLayout}
                name="PlannedContainerActionList"
                columns={columns}
                loader={loadPage}
                refetchRef={refetchRef}
                toolbarElements={toolbarElements}
                objectSelector={canFollowUp ? groupSelector : undefined}
                renderSubComponent={(group) => (
                    <ContainerStockLines
                        name="planned_container_action_lines"
                        group={group}
                        columns={lineColumns}
                        objectSelector={canFollowUp ? lineSelectorOf(group) : undefined}
                    />
                )}
                disableExpanding={hasNoLinesToExpand}
                forceExpand={{ expanded, setExpanded }}
                onSelect={(group) => {
                    if (!hasNoLinesToExpand(group)) {
                        setExpanded((current) => toggledExpansion(current, group.id));
                    }

                    return true;
                }}
                canCreate={false}
                fullSize
            />
            {canFollowUp && selectedRows.length > 0 && (
                <FloatingToolbar>
                    <Badge variant="success" type="input">
                        {selectedRows.length}
                    </Badge>
                    <FollowUpButtons
                        allowedFollowUps={allowedFollowUps}
                        locked={isBusy}
                        onFollowUp={(kind) => void startFollowUp(kind)}
                    />
                </FloatingToolbar>
            )}
        </>
    );
}
